import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import App from './App'
import ReportsApp, { isReportsLocation } from './reports/ReportsApp'
import './styles.css'
import './phone.css'
import { markDevice } from './lib/device'
import { watchKeyboard } from './lib/keyboard'
import { registerSW } from 'virtual:pwa-register'

// The iPad keeps the app open for days: look for a new build every few minutes and whenever the app comes back to the front,
// then swap to it (the page reloads itself once the new worker is in control).
registerSW({
  immediate: true,
  onRegisteredSW(_url, r) {
    if (!r) return
    const check = () => { if (navigator.onLine) r.update().catch(() => {}) }
    setInterval(check, 5 * 60 * 1000)
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check() })
  },
})

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    environment: (import.meta.env.VITE_APP_ENV as string) || 'staging',
    release: import.meta.env.VITE_RELEASE as string | undefined,
    sendDefaultPii: false,
    // Speed and Feel S0: time one session in five (page load, screen changes, the counter.scan span) to see where the wait is.
    // No trace headers on requests (tracePropagationTargets empty): the functions' CORS rules would refuse them.
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
    tracePropagationTargets: [],
  })
}

// Reports (reports.starchivedesk.store, or /reports on the other hosts) is the desktop accounting screen: same bundle,
// its own screens, opened with the Reports code instead of an employee ID. Nothing below that is for the iPad runs there.
const reports = isReportsLocation()
if (!reports) markDevice()

if (!reports) {
  // Speed and Feel S3: keep the functions a scan or a sale calls warm while the app is open, so the first scan after a quiet
  // spell does not wait for a cold start. A bare GET with ?ping: the function answers ok before doing anything else.
  const HOT = ['product-lookup', 'product-search', 'sale-create', 'qa-lookup', 'qa-queue', 'qa-record', 'qa-owner']
  const warm = () => {
    const base = import.meta.env.VITE_SUPABASE_URL as string | undefined
    if (!base || !navigator.onLine || document.visibilityState !== 'visible') return
    for (const fn of HOT) fetch(`${base}/functions/v1/${fn}?ping=1`, { method: 'GET', cache: 'no-store' }).catch(() => {})
  }
  setInterval(warm, 4 * 60 * 1000)
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') warm() })
  warm()
  watchKeyboard()
}
// Safari only applies :active on touch when a touchstart listener exists somewhere on the page (F1, press states).
document.addEventListener('touchstart', () => {}, { passive: true })
createRoot(document.getElementById('root')!).render(<StrictMode>{reports ? <ReportsApp /> : <App />}</StrictMode>)
